/**
 * City Card Component
 * Displays a single city with status and country info
 */

import { useTranslation, getLocalizedValue } from '@ufc/core'
import {
  Card,
  Badge,
  Button,
  Building2,
  Globe,
  Settings,
  CheckCircle2,
  XCircle,
  cn,
} from '@ufc/uikit'
import type { CityListItem } from '../../domain/types'
import { CITY_STATUS_CONFIG } from '../../domain/constants'

// ============================================
// Helper Functions
// ============================================

function formatDate(dateString: string, locale: string): string {
  try {
    return new Date(dateString).toLocaleDateString(locale === 'ar' ? 'ar-SA' : 'en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    })
  } catch {
    return '-'
  }
}

function getStatusKey(isActive: boolean): 'active' | 'inactive' {
  return isActive ? 'active' : 'inactive'
}

// ============================================
// Types
// ============================================

export interface CityCardProps {
  city: CityListItem
  onSelect?: (city: CityListItem) => void
  onSettings?: (city: CityListItem) => void
}

// ============================================
// Component
// ============================================

export function CityCard({
  city,
  onSelect,
  onSettings,
}: CityCardProps) {
  const { t, i18n } = useTranslation()
  const isArabic = i18n.language === 'ar' // Keep for content selection (Arabic text vs English)

  const statusKey = getStatusKey(city.isActive)
  const statusConfig = CITY_STATUS_CONFIG[statusKey]

  return (
    <Card
      className={cn(
        'glass-card p-6',
        onSelect && 'cursor-pointer hover:ring-2 hover:ring-primary/20'
      )}
      onClick={() => onSelect?.(city)}
    >
      {/* Header */}
      <div className="flex items-start justify-between mb-4">
        <div className="flex-1 min-w-0">
          <h3 className="text-lg font-semibold text-foreground truncate">
            {getLocalizedValue(city.name, i18n.language)}
          </h3>
          <p className="text-sm text-muted-foreground font-mono">{city.code}</p>
        </div>
        <div className="flex items-center gap-2 ms-3">
          <Badge
            variant="secondary"
            className={cn('gap-1', statusConfig.bgClass, statusConfig.textClass)}
          >
            {city.isActive ? (
              <CheckCircle2 className="w-3 h-3" />
            ) : (
              <XCircle className="w-3 h-3" />
            )}
            {t(city.isActive ? 'cities.active' : 'cities.inactive')}
          </Badge>
          {onSettings && (
            <Button
              variant="ghost"
              size="icon"
              className="shrink-0"
              onClick={(e) => {
                e.stopPropagation()
                onSettings(city)
              }}
            >
              <Settings className="w-4 h-4" />
            </Button>
          )}
        </div>
      </div>

      {/* Location Info */}
      <div className="space-y-2 mb-4 min-h-[72px]">
        {/* Country */}
        <div className="flex items-center gap-2 text-sm">
          <Globe className="w-4 h-4 text-muted-foreground shrink-0" />
          {city.countryName ? (
            <span className="text-foreground">
              {city.countryCode && (
                <span className="text-muted-foreground me-1">[{city.countryCode}]</span>
              )}
              {city.countryName}
            </span>
          ) : (
            <span className="text-muted-foreground/60 italic">
              {isArabic ? 'لم يتم تحديد البلد' : 'No country assigned'}
            </span>
          )}
        </div>

        {/* State/Province */}
        {city.stateProvince && (
          <div className="flex items-center gap-2 text-sm">
            <Building2 className="w-4 h-4 text-muted-foreground shrink-0" />
            <span className="text-foreground">
              {getLocalizedValue(city.stateProvince, i18n.language)}
            </span>
          </div>
        )}
      </div>

      {/* Footer - Timestamps */}
      <div className="flex items-center justify-between text-xs text-muted-foreground pt-3 border-t border-border/50">
        <span>
          {isArabic ? 'تم الإنشاء' : 'Created'}: {formatDate(city.createdAt, i18n.language)}
        </span>
        {city.updatedAt && city.updatedAt !== city.createdAt && (
          <span>
            {isArabic ? 'تم التحديث' : 'Updated'}: {formatDate(city.updatedAt, i18n.language)}
          </span>
        )}
      </div>
    </Card>
  )
}
